import { useEffect, useState } from "react";
import { Brain, ChevronRight, CheckCircle, XCircle, Loader, Sparkles, RotateCcw } from "lucide-react";
import { quizApi } from "../../lib";

interface AiPrepModalProps {
  isOpen: boolean;
  onClose: () => void;
  quizId: string;
  quizTitle: string;
  onRetakeReady?: () => void;
}

interface PrepQuestion {
  questionText: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  source: string;
}

const AiPrepModal = ({ isOpen, onClose, quizId, quizTitle, onRetakeReady }: AiPrepModalProps) => {
  const [loading, setLoading] = useState(false);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<PrepQuestion[]>([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<boolean | null>(null);
  const [explanation, setExplanation] = useState("");
  const [history, setHistory] = useState<{ index: number; correct: boolean }[]>([]);
  const [score, setScore] = useState<{ earned: number; total: number; percentage: number } | null>(null);
  const [completed, setCompleted] = useState(false);
  const [progress, setProgress] = useState({ answered: 0, total: 0 });
  const [errorCount, setErrorCount] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(false);
    setSubmittingAnswer(false);
    setError(null);
    setErrorCount(0);
  }, [isOpen]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setErrorCount(0);
    try {
      const response = await quizApi.generatePrepQuestions(quizId);
      if (response.success) {
        setQuestions(response.data?.questions ?? []);
        setProgress({ answered: 0, total: response.data?.totalQuestions ?? 5 });
        setCurrent(0);
        setHistory([]);
        setScore(null);
        setCompleted(false);
        setSubmitted(false);
        setResult(null);
        setSelected(null);
        setExplanation("");
      } else {
        setError(response?.message || "Failed to generate prep drill.");
        setQuestions([]);
        setProgress({ answered: 0, total: 0 });
      }
    } catch (err: any) {
      setError(err.message || "Failed to generate prep drill. Please try again.");
      setQuestions([]);
      setProgress({ answered: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selected === null || !questions[current] || submittingAnswer || completed) return;
    setSubmittingAnswer(true);
    const currentSelected = selected;
    try {
      const payload = await quizApi.submitPrepAnswer(quizId, current, currentSelected);
      const isCorrect = payload.data?.isCorrect ?? false;
      setResult(isCorrect);
      setExplanation(payload.data?.explanation || "");
      setSubmitted(true);
      setHistory((prev) => [...prev, { index: current, correct: isCorrect }]);
      setScore(payload.data?.score ?? null);
      setErrorCount(0);

      if (payload.data?.progress) {
        setProgress(payload.data.progress);
      }

      if (payload.data?.completed) {
        setCompleted(true);
        setScore(payload.data?.score ?? null);
      }
    } catch (err: any) {
      setError(err.message || "Failed to save answer");
      setErrorCount((c) => c + 1);
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const next = () => {
    if (current < questions.length - 1) {
      setCurrent((p) => p + 1);
      setSelected(null);
      setSubmitted(false);
      setResult(null);
      setExplanation("");
      setError(null);
    }
  };

  const retakePrep = () => {
    setCurrent(0);
    setSelected(null);
    setSubmitted(false);
    setResult(null);
    setHistory([]);
    setScore(null);
    setCompleted(false);
    setProgress({ answered: 0, total: questions.length });
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-semibold">AI Prep Drill</h3>
            </div>
            <p className="text-[10px] text-gray-500 truncate">{quizTitle}</p>
          </div>
          <button onClick={onClose} className="text-[10px] text-gray-500 px-2 py-1">
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!questions.length ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <p className="text-xs text-gray-600 mb-1">
                Boost your quiz score with a 5-question mini-drill tailored to your weak spots.
              </p>
              <p className="text-[10px] text-gray-400 mb-4">
                Works offline using your past quiz results.
              </p>
              {error && <p className="text-[10px] text-red-500 mb-3">{error}</p>}
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="text-[11px] text-white bg-primary px-5 py-2.5 rounded-lg inline-flex items-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                {loading ? "Generating prep..." : "Generate Prep Drill"}
              </button>
            </div>
          ) : (
            <>
              {!completed && (
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] text-gray-500">
                    Q{current + 1} of {questions.length} · {progress.answered} answered
                  </p>
                  {score && (
                    <span className="text-[10px] font-mono font-semibold text-primary">
                      {score.earned}/{score.total}
                    </span>
                  )}
                </div>
              )}

              {!completed ? (
                <div>
                  <p className="text-xs leading-relaxed text-gray-800 mb-4">
                    {questions[current]?.questionText}
                  </p>

                  <div className="space-y-2 mb-4">
                    {questions[current]?.options.map((opt, idx) => {
                      const isSelected = selected === idx && !submitted;
                      const isCorrectChoice = submitted && idx === questions[current].correctIndex;
                      const isWrongChoice = submitted && isSelected && result === false;
                      return (
                        <button
                          key={idx}
                          type="button"
                          disabled={submitted || submittingAnswer}
                          onClick={() => {
                            if (!submitted && !submittingAnswer) setSelected(idx);
                          }}
                          className={`w-full text-left text-[11px] px-3 py-2.5 rounded-lg border transition-all ${
                            isCorrectChoice
                              ? "bg-green-50 border-green-500 text-green-800"
                              : isWrongChoice
                                ? "bg-red-50 border-red-400 text-red-800"
                                : isSelected
                                  ? "bg-primary/10 border-primary"
                                  : "bg-white border-gray-200 hover:border-gray-400"
                          }`}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {isCorrectChoice && <CheckCircle className="w-3 h-3" />}
                            {isWrongChoice && <XCircle className="w-3 h-3" />}
                            {opt}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {submitted && result ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                      <p className="text-[11px] text-green-800">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Correct!
                      </p>
                    </div>
                  ) : null}

                  {submitted && !result && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                      <p className="text-[11px] text-red-800">
                        <XCircle className="w-4 h-4 inline mr-1" />
                        Not quite — review the explanation below.
                      </p>
                    </div>
                  )}

                  {submitted && explanation && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <p className="text-[10px] text-blue-800">{explanation}</p>
                    </div>
                  )}

                  {error && !submitted && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                      <p className="text-[10px] text-red-800">{error}</p>
                    </div>
                  )}

                  <div className="flex justify-between pt-2">
                    <button
                      type="button"
                      onClick={retakePrep}
                      className="text-[10px] text-gray-600 inline-flex items-center gap-1.5"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Retry Drill
                    </button>
                    {!completed ? (
                      <button
                        type="button"
                        onClick={submitted ? next : handleSubmit}
                        disabled={!submitted && (selected === null || submittingAnswer)}
                        className="text-[11px] text-white bg-primary px-4 py-2 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {submittingAnswer ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Loader className="w-3.5 h-3.5 animate-spin" />
                            Saving
                          </span>
                        ) : submitted && current < questions.length - 1 ? (
                          <>
                            Next
                            <ChevronRight className="w-3.5 h-3.5" />
                          </>
                        ) : submitted ? (
                          "See Score"
                        ) : (
                          "Submit"
                        )}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${
                      (score?.percentage ?? 0) >= 60 ? "bg-green-100" : "bg-amber-100"
                    }`}
                  >
                    {(score?.percentage ?? 0) >= 60 ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <Brain className="w-8 h-8 text-amber-500" />
                    )}
                  </div>
                  <p className="text-sm font-semibold mb-1">Drill Complete</p>
                  <p className="text-xs text-gray-600 mb-3">
                    {score?.earned} / {score?.total} · {score?.percentage}%
                  </p>
                  <div className="flex gap-2 justify-center">
                    <button
                      type="button"
                      onClick={retakePrep}
                      className="text-[11px] bg-white border border-gray-200 px-3.5 py-2 rounded-lg inline-flex items-center gap-1.5"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Retake Drill
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onRetakeReady?.();
                      }}
                      className="text-[11px] text-white bg-primary px-4 py-2 rounded-lg inline-flex items-center gap-1.5"
                    >
                      Go to Quiz <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiPrepModal;
